// dependencies
import React, { useEffect, useMemo } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

// schema
import { SurveyModel, TSurveyModel } from "@performyard/models";

// components
import Button from "../../../../../components/Button";
import OptionsDrawer from "../../../../../components/OptionsDrawer";
import Pagination from "../../../../../components/Pagination";
import Spacer from "../../../../../components/Spacer";
import ScrollableTable from "../../../../../components/TableLite/TableLiteScrollable";
import SurveysModals from "../components/SurveysModals";

import { SURVEY_MODALS } from "../store/model";
// store
import { useSetters, useSurveysPageTab } from "../store/store";

import { useInternalApiSearchService } from "../../../../../api/hooks/defs/search-service";

// page template components
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
// config
import { SURVEYS_TABLE_LIMIT, surveysConfig } from "../config";

const ListSurveys = () => {
	// store
	const setters = useSetters();
	const surveysPageTab = useSurveysPageTab();

	// hooks
	const { pageNumber, setPageNumber, sort, setSort, searchValue, setDebouncedSearch, debouncedSearchValue } =
		useInfoPageTemplateQueryState<Record<string, unknown>>({
			defaultSortName: "start_date",
			defaultSortDirection: "desc",
			defaultPageNumber: 1,
		});
	const {
		data: surveys,
		isLoading,
		refetch,
	} = useInternalApiSearchService({
		service: "survey",
		filters: {
			name_re: debouncedSearchValue,
		},
		skip: (pageNumber - 1) * SURVEYS_TABLE_LIMIT,
		limit: SURVEYS_TABLE_LIMIT,
		sort: {
			name: sort.name,
			direction: sort.direction,
		},
	});

	const parsedSurveysResults = useMemo(() => {
		return (surveys?.results || []).map((survey) => {
			return SurveyModel.parse(survey);
		});
	}, [surveys]);

	useEffect(() => {
		if (!isLoading) {
			refetch();
		}
	}, []);

	// handlers
	const handleClickCreateSurvey = () => {
		setters.setModal("create-survey");
	};
	const handleOptionSelection = (option: { id: string; label: string }, survey: TSurveyModel) => {
		setters.setSurveyId(survey._id.toString());
		setters.setSurveyLink(survey.surveyLink ? `${window.location.origin}${survey.surveyLink}` : "");
		setters.setSurveyName(survey.name);
		if (option.id === "copy-link") {
			setters.setModal(SURVEY_MODALS.SURVEY_LINK);
		} else if (option.id === "copy") {
			setters.setModal(SURVEY_MODALS.COPY_SURVEY);
		} else if (option.id === "close") {
			setters.setModal(SURVEY_MODALS.CLOSE_SURVEY);
		} else if (option.id === "delete") {
			setters.setModal(SURVEY_MODALS.DELETE_SURVEY);
		}
	};

	// page template elements:
	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={setDebouncedSearch}
			searchPlaceholder="Search surveys..."
		/>
	);
	const columns = [
		...surveysConfig.allSurveysStaticTableCols,
		// add the meatball column to the static columns
		{
			id: "options",
			label: "",
			width: 40,
			isSortable: false,
			getter: (survey: TSurveyModel) => {
				const { status } = survey;
				const options = [
					...(["active", "scheduled"].includes(status)
						? [{ id: "copy-link", label: "Copy Survey Link" }]
						: []),
					{ id: "copy", label: "Save as Copy" },
					...(["active", "scheduled"].includes(status) ? [{ id: "close", label: "Close Survey" }] : []),
					{ id: "delete", label: "Delete Survey" },
				];
				return (
					<div className="flex justify-end">
						<OptionsDrawer
							horizontal
							optionWidth={110}
							options={options}
							openOn="bottom-end"
							onSelect={(option) => handleOptionSelection(option, survey)}
						/>
					</div>
				);
			},
		},
	];
	const mainContent = (
		<div data-testid="list-surveys-table">
			<ScrollableTable
				columns={columns}
				rows={parsedSurveysResults}
				isLoading={isLoading}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				onColumnClick={(column: string) =>
					setSort({ name: column, direction: sort.direction === "asc" ? "desc" : "asc" })
				}
				emptyChildren={<EmptySearchTableMessage mainText="No Surveys Found" />}
			/>
			<Spacer height={3} />
			<Pagination
				pageCount={Math.ceil((surveys?.total || SURVEYS_TABLE_LIMIT) / SURVEYS_TABLE_LIMIT)}
				currentPage={pageNumber}
				onClick={(page: number) => setPageNumber(page)}
			/>
			<Spacer height={2} />
		</div>
	);

	// render:
	return (
		<>
			<InfoPageTemplate
				navigationLinks={surveysConfig.navLinks}
				actionButtons={[
					<Button key="create-survey" color="green" icon={faPlus} onClick={handleClickCreateSurvey}>
						Create Survey
					</Button>,
				]}
				toolbarActionRow={toolbarActionRow}
				activeNavigationLinkKey={surveysPageTab.key}
				mainContent={mainContent}
				pageTitle="Surveys"
			/>
			<SurveysModals refetch={refetch} />
		</>
	);
};

export default ListSurveys;
